import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Portal from "@mui/material/Portal";
import Typography from "@mui/material/Typography";
import useShortcut from "common/utilities/hooks/useShortcut/useShortcut";
import theme, { sx, ThemeColors } from "core/utilities/theme";
import React, { useEffect, useState } from "react";

import classes from "./Loading.style";

export interface ComponentProps {
  label?: string;
  inBackdrop?: boolean;
  testid?: string;
  hideSeconds?: boolean;
}

function Loading({ label, inBackdrop, testid, hideSeconds }: ComponentProps) {
  const [seconds, setSeconds] = useState(0);
  const isSlow = () => seconds >= 3 && !hideSeconds;

  // Catch all ctrl+s temporarly while the loader is active to ensure the browser
  // doesn't trigger the page save functionality that might interfer with async calls
  useShortcut("ctrl+s", true);

  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds((s) => s + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [seconds]);

  useEffect(() => {
    if (inBackdrop) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.removeProperty("overflow");
    };
  }, [inBackdrop]);

  const Content = (
    <Box
      data-testid={testid}
      display="flex"
      justifyContent="center"
      alignItems="center"
      textAlign="center"
      height="100%"
      width="100%"
      position={"fixed"}
    >
      <Box sx={inBackdrop ? classes.inBackdrop : undefined}>
        <Box position="relative" display="inline-flex">
          <CircularProgress
            size={80}
            sx={sx(
              inBackdrop ? { color: ThemeColors.backdropBg } : undefined,
              isSlow() ? classes.loaderSlow : undefined,
            )}
          />
          {isSlow() && (
            <Box
              top={0}
              left={0}
              bottom={0}
              right={0}
              position="absolute"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Typography
                variant="caption"
                component="div"
                sx={{ color: inBackdrop ? ThemeColors.backdropBg : theme.palette.text.secondary }}
              >
                {`${seconds}s`}
              </Typography>
            </Box>
          )}
        </Box>
        {label && (
          <Box>
            <Typography
              component="span"
              sx={sx({ color: inBackdrop ? ThemeColors.backdropBg : theme.palette.text.primary }, classes.title)}
            >
              {label}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  if (inBackdrop) {
    return (
      <Portal>
        <Backdrop sx={{ zIndex: 9999 }} open>
          {Content}
        </Backdrop>
      </Portal>
    );
  }

  return Content;
}

export default Loading;
